import React, {useCallback, useEffect, useState} from 'react';
import {CardMedia} from '@mui/material';
import {MatrixEvent} from 'matrix-js-sdk';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import DialogWithCloseIcon from '@/components/ui/dialogs/dialog-with-close-icon';
import ImageThumbnails from './image-thumbnail';
import {useMatrixContext} from '@/contexts/matrix/client';
import {getUrlsFromEvents} from '@/components/matrix-chat/utils/helper-functions';
import BoxWithTwoElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-two-elements';
import BoxWithThreeElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-three-elements';
import BoxWithFourElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-four-elements';
import BoxWithFiveElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-five-elements';
import BoxWithMoreThanFiveElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-more-than-five-elements';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import {useTranslation} from 'react-i18next';
import ContentDisplayBox from '@/components/matrix-chat/ui/content-display-box';
import {
  AdditionalMultimediaInfo,
  EventPosition,
  Multimedia,
  MultimediaDownloadInfo
} from '@/types/matrix';
import BoxWithOneElement from '@/components/matrix-chat/ui/multimedia-boxes/box-with-one-element';

interface MultiImageBoxProps {
  imageEvent: MatrixEvent;
  eventPosition: EventPosition;
}

const MultiImageBox: React.FC<MultiImageBoxProps> = ({
  imageEvent,
  eventPosition
}) => {
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [activeImageInfo, setActiveImageInfo] =
    useState<MultimediaDownloadInfo | null>(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [imageDeletionStarted, setImageDeletionStarted] = useState(false);

  const {matrixUserId} = useMatrixContext();

  const {
    handleOpenMultimediaDialog,
    handleCloseMultimediaDialog,
    handleDeleteMatrixEvent,
    handleDownloadSingleMultimedia,
    handleDownloadMultipleMultimedia,
    handleDeleteImage
  } = useMatrixRoomContext();

  const {t} = useTranslation();

  const effectiveEvent = imageEvent.getEffectiveEvent();

  const text = effectiveEvent.content.body;

  const isOwnMessage = effectiveEvent.sender === matrixUserId;

  const isSingleImageMessage = imageUrls.length === 1;

  const eventMimeType = effectiveEvent.content.info.mimetype;

  const handleOpenDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(true);
  }, []);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleGetMultimediaDownloadInfo = useCallback(
    (url: string) => {
      const urlSplit = url.split('/');
      const lastUrlElement = urlSplit[urlSplit.length - 1];
      const additionalInfo = effectiveEvent.content.additional.find(
        (item: AdditionalMultimediaInfo) => {
          const urlSplit = item.url.split('/');
          const urlSuffixFromEvent = urlSplit[urlSplit.length - 1];

          return urlSuffixFromEvent === lastUrlElement;
        }
      );
      return {
        type: 'image' as Multimedia,
        url,
        mimeType: additionalInfo ? additionalInfo.info.mimetype : eventMimeType
      };
    },
    [effectiveEvent, eventMimeType]
  );

  const handleDownloadInIconPanel = useCallback(() => {
    if (imageUrls.length === 1) {
      handleDownloadSingleMultimedia({
        url: imageUrls[0],
        type: 'image',
        mimeType: eventMimeType
      });
    } else {
      const multimediaDownloadInfo = imageUrls.map((url) => {
        return handleGetMultimediaDownloadInfo(url);
      });

      handleDownloadMultipleMultimedia(multimediaDownloadInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveEvent,
    eventMimeType,
    handleDownloadMultipleMultimedia,
    handleDownloadSingleMultimedia,
    imageUrls
  ]);

  const handleDeleteClick = useCallback(async () => {
    try {
      await handleDeleteMatrixEvent(imageEvent);

      handleCloseDeleteConfirmation();
    } catch (error) {
      console.error('Failed to delete the message:', error);
    }
  }, [handleCloseDeleteConfirmation, handleDeleteMatrixEvent, imageEvent]);

  const handleOpenFullScreen = useCallback(
    (imageUrl: string) => {
      const imageInfo = handleGetMultimediaDownloadInfo(imageUrl);
      setActiveImageInfo(imageInfo);

      handleOpenMultimediaDialog();
    },
    [handleGetMultimediaDownloadInfo, handleOpenMultimediaDialog]
  );

  const handleCloseFullScreen = useCallback(() => {
    setActiveImageInfo(null);
    handleCloseMultimediaDialog();
  }, [handleCloseMultimediaDialog]);

  const onSigleImageDeleted = useCallback(() => {
    setImageDeletionStarted(true);
    handleCloseFullScreen();
  }, [handleCloseFullScreen]);

  const renderImageLayout = useCallback(
    (imageCount: number) => {
      switch (imageCount) {
        case 1:
          return (
            <BoxWithOneElement
              urls={imageUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="image"
            />
          );
        case 2:
          return (
            <BoxWithTwoElements
              urls={imageUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="image"
            />
          );
        case 3:
          return (
            <BoxWithThreeElements
              urls={imageUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="image"
            />
          );

        case 4:
          return (
            <BoxWithFourElements
              urls={imageUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="image"
            />
          );
        case 5:
          return (
            <BoxWithFiveElements
              urls={imageUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="image"
            />
          );

        default:
          return (
            <BoxWithMoreThanFiveElements
              urls={imageUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="image"
            />
          );
      }
    },
    [handleOpenFullScreen, imageUrls]
  );

  useEffect(() => {
    if (imageEvent || imageDeletionStarted) {
      const contentFromImageEvent = imageEvent.getEffectiveEvent().content;

      if (
        contentFromImageEvent &&
        'url' in contentFromImageEvent &&
        'info' in contentFromImageEvent &&
        'additional' in contentFromImageEvent
      ) {
        const newContent = {
          url: contentFromImageEvent.url,
          info: contentFromImageEvent.info,
          additional: contentFromImageEvent.additional,
          body: contentFromImageEvent.body || ''
        };

        const urls = getUrlsFromEvents(newContent);
        setImageUrls(urls);
        setImageDeletionStarted(false);
      } else {
        setImageUrls([]);
        setImageDeletionStarted(false);
      }
    }
  }, [imageDeletionStarted, imageEvent]);

  return (
    <>
      <ContentDisplayBox
        event={imageEvent}
        eventPosition={eventPosition}
        messageText={text}
        onDownloadIconPanel={handleDownloadInIconPanel}
        onDeleteIconPanel={handleOpenDeleteConfirmation}
      >
        {renderImageLayout(imageUrls.length)}
        {activeImageInfo && imageEvent && (
          <DialogWithCloseIcon
            open={Boolean(activeImageInfo)}
            onClose={handleCloseFullScreen}
            chatMode={true}
            multimediaInfo={activeImageInfo}
            onDownload={handleDownloadSingleMultimedia}
            onDeleteEvent={handleDeleteImage}
            matrixEvent={imageEvent}
            isOwnMessage={isOwnMessage}
            onDeleted={onSigleImageDeleted}
            customDialogTitle={'delete_image_title'}
            customDialogContent={'delete_image_dialog_content'}
          >
            <CardMedia
              component="img"
              image={activeImageInfo?.url}
              alt="image"
              sx={{
                maxWidth: '100%',
                maxHeight: isSingleImageMessage ? '75vh' : '60vh',
                objectFit: 'contain'
              }}
            />
            {imageUrls && !isSingleImageMessage && activeImageInfo && (
              <ImageThumbnails
                selectedImage={activeImageInfo.url}
                allUrls={imageUrls}
                onSelectImage={(url: string) => {
                  const activeImage = handleGetMultimediaDownloadInfo(url);
                  setActiveImageInfo(activeImage);
                }}
              />
            )}
          </DialogWithCloseIcon>
        )}

        <ConfirmationDialog
          open={deleteConfirmationOpen}
          title={t('delete_image_multiple_title')}
          message={t('delete_image_multiple_dialog_content')}
          onConfirm={handleDeleteClick}
          onCancel={handleCloseDeleteConfirmation}
        />
      </ContentDisplayBox>
    </>
  );
};

export default MultiImageBox;
